import axios from 'axios';
import i18n from '../i18n/config';

/**
 * Client for the Medhabi Question Bank public API (read-only).
 *
 * This is a SEPARATE backend from the main SohozKaj API, reached directly from
 * the browser with a static public key. The key is non-sensitive (it only
 * unlocks read access to question content) and ships in the client bundle by
 * design — see VITE_QBANK_KEY in .env.
 *
 * Response envelope: { success, message, data }. We unwrap to `data`.
 */
const BASE_URL = import.meta.env.VITE_QBANK_URL || 'http://localhost:4001';
const API_KEY = import.meta.env.VITE_QBANK_KEY || '';

const qbank = axios.create({
    baseURL: `${BASE_URL}/api/tools/question-bank`,
    headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
    },
});

qbank.interceptors.response.use(
    (res) => res.data,
    (error) => {
        // Show a friendly, translated message for rate-limiting (429) instead of
        // the raw English "Rate limit exceeded" from the question-bank API.
        const message = error?.response?.status === 429
            ? i18n.t('questionsCreate.rateLimit')
            : (error?.response?.data?.message || i18n.t('errors.networkError'));
        return Promise.reject(new Error(message));
    },
);

const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);

const questionBankService = {
    /**
     * Filter tree for the dependent Class → Subject → Chapter dropdowns.
     * @returns {Promise<{ classes: Array<{ value: string, subjects: Array<{ value: string, chapters: string[] }> }> }>}
     */
    getFilters: async () => {
        const res = await qbank.get('/filters');
        return unwrap(res) || { classes: [] };
    },

    /**
     * Query questions with server-side filtering + pagination.
     * @param {{ class?: string, subject?: string, chapter?: string, type?: string, search?: string, page?: number, limit?: number }} params
     * @returns {Promise<{ items: Array<object>, pagination: { page: number, limit: number, total: number, totalPages: number } }>}
     */
    getQuestions: async (params = {}) => {
        // Drop empty values so we don't send `?class=&subject=` etc.
        const query = Object.fromEntries(
            Object.entries(params).filter(([, v]) => v !== '' && v != null),
        );
        const res = await qbank.get('/questions', { params: query });
        return unwrap(res) || { items: [], pagination: { page: 1, limit: 0, total: 0, totalPages: 0 } };
    },
};

export default questionBankService;
